import React, { useRef, useReducer, useEffect } from "react"
import moment from "moment"
import PropTypes from "prop-types"
import Input from "../../../../Input/Input"
import Calendar from "../../../../Calendar/Calendar"
import useCalendarWidth from "./useCalendarWidth"
import useClickOutsideCalendar from "./useClickOutsideCalendar"
import { reducer, initState } from "./reducer"
import {
  openFromDate,
  openToDate,
  setDates,
  closeCalendar,
} from "./actionCreators"
import { FILTER_DATE_FORMAT } from "../../../../../helpers/dateHandlers"
import styles from "./index.module.scss"

const EARLIEST_DATE = moment("2020-01-01")

// `maxDate` defaults to today; pass null for no upper bound.
const DatePicker = ({
  title = "Opportunity Creation Date",
  fromDate,
  toDate,
  onChangeFrom,
  onChangeTo,
  minDate = EARLIEST_DATE,
  maxDate = moment(),
}) => {
  const calendarRef = useRef()
  const [state, dispatch] = useReducer(reducer, initState)
  const [pickerRef, calendarWidth] = useCalendarWidth(282)
  useClickOutsideCalendar(calendarRef, state.isCalendarOpen, () =>
    dispatch(closeCalendar()),
  )
  useEffect(() => {
    dispatch(setDates(fromDate, toDate))
  }, [fromDate, toDate])
  const isShortCalendar =
    (state.dateType === "to" && maxDate?.isSame(fromDate, "month")) ||
    (state.dateType === "from" && minDate.isSame(toDate, "month"))
  return (
    <div ref={pickerRef} className={styles.dateField}>
      <p className={styles.title}>{title}</p>
      <Input
        className={styles.date}
        type="text"
        placeholder="From"
        value={fromDate?.format(FILTER_DATE_FORMAT)}
        onClick={() => dispatch(openFromDate(minDate, maxDate))}
        readOnly
      />
      <span className={styles.dash}>-</span>
      <Input
        className={styles.date}
        type="text"
        placeholder="To"
        value={toDate?.format(FILTER_DATE_FORMAT)}
        onClick={() => dispatch(openToDate(minDate, maxDate))}
        readOnly
      />
      {state.isCalendarOpen && (
        <div className={styles.calendar} ref={calendarRef}>
          <Calendar
            autoFocus={false}
            selected={state.selectedDate}
            width={calendarWidth}
            height={isShortCalendar ? 280 : 500}
            min={state.minDate}
            max={state.maxDate}
            minDate={state.minDate}
            maxDate={state.maxDate}
            onChange={(selectedDate) => {
              if (state.dateType === "from") {
                onChangeFrom(moment(selectedDate))
              } else {
                onChangeTo(moment(selectedDate))
              }
              dispatch(closeCalendar())
            }}
          />
        </div>
      )}
    </div>
  )
}

DatePicker.propTypes = {
  title: PropTypes.string,
  fromDate: PropTypes.object,
  toDate: PropTypes.object,
  onChangeFrom: PropTypes.func.isRequired,
  onChangeTo: PropTypes.func.isRequired,
  /** moment; earliest selectable date */
  minDate: PropTypes.object,
  /** moment or null for no upper bound */
  maxDate: PropTypes.object,
}

export default DatePicker
